import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { productService, categoryService } from "../services/api";
import ProductCard from "../components/ProductCard";
import Loading from "../components/Loading";
import { FiTruck, FiCreditCard, FiHeadphones, FiArrowRight, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { AiOutlineClockCircle } from "react-icons/ai";
import hero2 from "../assets/images/hero2.webp";

const Home = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const newArrivalsRef = useRef(null);
  const categoriesRef = useRef(null);
  const categorySliderRef = useRef(null);
  const servicesRef = useRef(null);

  const heroSlides = [
    {
      title: "Collection Nouvelle Saison",
      subtitle: "Découvrez les dernières tendances",
      description: "Profitez de -30% sur toute la collection",
      bg: "from-gray-900 to-gray-700",
      image: "https://t3.ftcdn.net/jpg/07/53/66/42/360_F_753664229_1o0xZyAZOKzDAYKlCQJPdTi0pt38C6Zp.jpg?w=1200",
    },
    {
      title: "Livraison Gratuite",
      subtitle: "Sur toutes vos commandes",
      description: "Livraison en 1-2 jours partout au Maroc",
      bg: "from-gray-900 to-gray-700",
      image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200",
    },
    {
      title: "Qualité Premium",
      subtitle: "Produits sélectionnés",
      description: "Les meilleures marques au meilleur prix",
      bg: "from-gray-900 to-gray-700",
      image: hero2,
    },
  ];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cats, newArr] = await Promise.all([
        categoryService.getCategories(),
        productService.getProducts({ limit: 12, sort: "-created_at" }),
      ]);

      setCategories(cats.data || []);
      setNewArrivals(newArr.data || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [loadData, heroSlides.length]);

  // Scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.1, rootMargin: "0px 0px -50px 0px" };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("animate-fade-in-up");
      });
    }, observerOptions);

    [categoriesRef, newArrivalsRef, servicesRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [categories, newArrivals]);

  const scrollCarousel = (ref, direction) => {
    if (!ref.current) return;
    const scrollAmount = 320;
    ref.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const CategoryCard = ({ category }) => {
    const categoryImage = category.image?.startsWith("http")
      ? category.image
      : category.image
        ? `https://pro-dash.orima.ma/${category.image}`
        : "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80";

    return (
      <div
        onClick={() => navigate(`/shop?category=${category.id}`)}
        className="group cursor-pointer flex-shrink-0 w-44 sm:w-52"
      >
        <div className="rounded-2xl overflow-hidden bg-gray-50 border border-gray-200 shadow-sm transition-all duration-300 group-hover:border-gray-900 group-hover:shadow-lg">
          <div className="aspect-[4/3]">
            <img
              src={categoryImage}
              alt={category.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80";
              }}
            />
          </div>

          <div className="p-3">
            <h3 className="text-center text-sm font-extrabold text-gray-900 group-hover:text-gray-700 transition-colors line-clamp-2">
              {category.name}
            </h3>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Loading />;

  return (
    <>
      <Helmet>
        <title>ORIMA - Boutique de Luxe en Ligne | Sacs, Accessoires & Mode Premium au Maroc</title>
        <meta
          name="description"
          content="Découvrez ORIMA, votre destination luxe au Maroc. Sacs designer, accessoires premium et montres de luxe. Livraison gratuite 1-2 jours partout au Maroc."
        />
        <meta
          name="keywords"
          content="ORIMA boutique luxe Maroc, sacs de luxe, portemonnaie et portefeuille luxe, montres premium, chaussures luxe, accessoires mode, livraison gratuite Maroc"
        />
        <link rel="canonical" href="https://pro-dash.orima.ma/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pro-dash.orima.ma/" />
        <meta property="og:title" content="ORIMA - Boutique de Luxe en Ligne au Maroc" />
        <meta property="og:description" content="Découvrez notre collection exclusive. Livraison gratuite au Maroc." />
        <meta property="og:image" content={hero2} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://pro-dash.orima.ma/" />
        <meta property="twitter:title" content="ORIMA - Boutique de Luxe en Ligne au Maroc" />
        <meta property="twitter:description" content="Découvrez notre collection exclusive. Livraison gratuite au Maroc." />
        <meta property="twitter:image" content={hero2} />
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
        {/* HERO */}
        <div className="relative h-[420px] sm:h-[520px] md:h-[620px] lg:h-[700px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${slide.bg} opacity-70`} />
              <img
                src={slide.image}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-black/30" />

              <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                <div className="max-w-2xl text-white">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-extrabold mb-3 sm:mb-4 lg:mb-6 leading-tight animate-fade-in-up">
                    {slide.title}
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-2 sm:mb-3 lg:mb-4 animate-fade-in-up animation-delay-200">
                    {slide.subtitle}
                  </p>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-5 sm:mb-7 lg:mb-9 text-white/90 animate-fade-in-up animation-delay-400">
                    {slide.description}
                  </p>

                  <button
                    onClick={() => navigate("/shop")}
                    className="bg-yellow-200 text-gray-900 px-6 sm:px-7 md:px-9 py-3 md:py-4 rounded-2xl font-extrabold text-sm sm:text-base md:text-lg hover:bg-yellow-300 transition-transform active:scale-95 btn-attire-yellow animate-fade-in-up animation-delay-600"
                  >
                    Découvrir maintenant
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Indicators */}
          <div className="absolute bottom-5 sm:bottom-7 lg:bottom-9 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-3 z-20">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide ? "w-8 bg-yellow-200" : "w-2 bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>

        {/* CATEGORIES */}
        {categories.length > 0 && (
          <div ref={categoriesRef} className="py-10 sm:py-12 md:py-16 bg-white opacity-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-10 md:mb-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 sm:mb-3 text-gray-900">
                  Nos Catégories
                </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600">Explorez notre sélection</p>
              </div>

              <div className="relative">
                <button
                  onClick={() => scrollCarousel(categorySliderRef, "left")}
                  className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-3 rounded-full border border-gray-200 shadow-lg hover:bg-gray-50 transition-all -ml-4"
                >
                  <FiChevronLeft className="w-6 h-6 text-gray-900" />
                </button>

                <button
                  onClick={() => scrollCarousel(categorySliderRef, "right")}
                  className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-3 rounded-full border border-gray-200 shadow-lg hover:bg-gray-50 transition-all -mr-4"
                >
                  <FiChevronRight className="w-6 h-6 text-gray-900" />
                </button>

                <div ref={categorySliderRef} className="flex gap-4 sm:gap-6 overflow-x-auto scroll-smooth hide-scrollbar pb-2">
                  {categories.map((category) => (
                    <CategoryCard key={category.id} category={category} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NEW ARRIVALS */}
        {newArrivals.length > 0 && (
          <div className="py-10 sm:py-12 md:py-16 bg-gray-50 opacity-0" ref={newArrivalsRef}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 text-gray-900">
                  Nouveautés
                </h2>
                <p className="text-sm sm:text-base text-gray-600">Nos derniers arrivages</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {newArrivals.slice(0, 8).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              <div className="text-center mt-8 sm:mt-10">
                <button
                  onClick={() => navigate("/shop")}
                  className="bg-yellow-200 text-gray-900 px-7 sm:px-9 py-3 rounded-2xl font-extrabold text-sm sm:text-base inline-flex items-center gap-2 hover:bg-yellow-300 transition-transform active:scale-95 btn-attire-yellow"
                >
                  Voir tous les produits
                  <FiArrowRight />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SERVICES */}
        <div ref={servicesRef} className="py-10 sm:py-12 md:py-16 bg-white opacity-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 text-gray-900">
                Pourquoi Nous Choisir
              </h2>
              <p className="text-sm sm:text-base text-gray-600">Une expérience d'achat exceptionnelle</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {[
                { icon: <FiTruck className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />, title: "Livraison Gratuite", desc: "Partout au Maroc", bg: "bg-green-100" },
                { icon: <AiOutlineClockCircle className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />, title: "Livraison Rapide", desc: "1 à 2 jours", bg: "bg-blue-100" },
                { icon: <FiCreditCard className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600" />, title: "Paiement à la livraison", desc: "100% sécurisé", bg: "bg-purple-100" },
                { icon: <FiHeadphones className="w-7 h-7 sm:w-8 sm:h-8 text-orange-600" />, title: "Support 24/7", desc: "Toujours disponible", bg: "bg-orange-100" },
              ].map((s, idx) => (
                <div key={idx} className="flex flex-col items-center text-center p-4 sm:p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 ${s.bg} rounded-full flex items-center justify-center mb-3 sm:mb-4`}>
                    {s.icon}
                  </div>
                  <h3 className="font-extrabold text-base sm:text-lg mb-1 text-gray-900">{s.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* STYLES */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }

          .animation-delay-200 { animation-delay: 0.2s; opacity: 0; }
          .animation-delay-400 { animation-delay: 0.4s; opacity: 0; }
          .animation-delay-600 { animation-delay: 0.6s; opacity: 0; }

          @keyframes attractYellow {
            0%, 100% { transform: scale(1); box-shadow: 0 10px 35px rgba(0,0,0,0.12); }
            50% { transform: scale(1.03); box-shadow: 0 18px 55px rgba(0,0,0,0.22); }
          }
          .btn-attire-yellow { animation: attractYellow 1.6s ease-in-out infinite; }
        `}</style>
      </div>
    </>
  );
};

export default Home;
